import { Observable, from, map } from 'rxjs';
import { injectable } from 'inversify';

import axios from 'src/app/axios';

import type { CancellationReasonResponse, RetentionEligibility } from '../../domain/entities/CancellationEntities';

export interface ICancellationApiService {
  submitCancellationReason: (
    workspaceId: string,
    reason: string,
    details?: string
  ) => Observable<CancellationReasonResponse>;
  checkRetentionEligibility: (workspaceId: string) => Observable<RetentionEligibility>;
  markRetentionOfferShown: (workspaceId: string) => Observable<RetentionEligibility>;
}

const baseURL = '/api/v1';

@injectable()
export default class CancellationApiService implements ICancellationApiService {
  submitCancellationReason(workspaceId: string, reason: string, details?: string) {
    const response = axios.post(
      `${baseURL}/workspaces/${workspaceId}/cancellation-reasons`,
      {
        reason,
        ...(details && { details }),
      }
    );

    return from(response).pipe(
      map((response) => {
        const data = response.data;
        return {
          uuid: data.uuid,
          reason: data.reason,
          details: data.details,
          platform: data.platform,
          subscriptionType: data.subscription_type,
          createdAt: data.created_at,
        } as CancellationReasonResponse;
      })
    );
  }

  checkRetentionEligibility(workspaceId: string) {
    const response = axios.get(
      `${baseURL}/workspaces/${workspaceId}/retention-offer/eligibility`
    );

    return from(response).pipe(
      map((response) => {
        const data = response.data;
        return {
          eligible: data.eligible,
          retentionOfferShown: data.retention_offer_shown,
          retentionOfferRedeemed: data.retention_offer_redeemed,
        } as RetentionEligibility;
      })
    );
  }

  markRetentionOfferShown(workspaceId: string) {
    const response = axios.post(
      `${baseURL}/workspaces/${workspaceId}/retention-offer/shown`
    );

    return from(response).pipe(
      map((response) => {
        const data = response.data;
        return {
          eligible: data.eligible,
          retentionOfferShown: data.retention_offer_shown,
          retentionOfferRedeemed: data.retention_offer_redeemed,
        } as RetentionEligibility;
      })
    );
  }
}
